/* eslint-disable @typescript-eslint/no-unused-vars */
import { Link } from 'components/Link';
import { AuditAPIDataType, useAuditLogData, useHasAuditLogAccess } from 'modules/UsageDashboard/TeamsAndLogs/hooks';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { IconCPU, IconModule, IconOrganization, IconOverview, IconServer, IconUser, IconWorkflow } from 'components/Icons';
import { dayjs, today } from 'modules/UsageDashboard/DateRangePicker';
import { IconProps } from 'components/Icons/IconProps';
import type { ApiAuditLogTarget } from 'types/swagger/clarifai-api';
import { Accordion, Loader, LoadingOverlay } from '@mantine/core';
import { apiErrorToReactLeft } from 'utils/errorHandling';
import { reactLeftToJSX } from 'utils/uiStates/uiStates';
import { cx } from '@linaria/core';
import { TextIcon } from 'components/TextIcon';
import { makeUserFullName } from 'utils/users';
import { makeInitials, stringToColor } from 'utils/strings/strings';
import { TextTooltip } from 'components/Tooltip';
import { HomeCard } from '../components/HomeCard';
import { link } from '../components/SetupComputeModal.styles';
import { darkArrow, groupedActivityListStyles } from './RecentActivity.styles';

enum AuditLogEventTypeGroup {
  UNKNOWN = 'UNKNOWN',
  ORGANIZATION = 'ORGANIZATION',
  MODULE = 'MODULE',
  MODEL = 'MODEL',
  WORKFLOW = 'WORKFLOW',
  APPLICATION = 'APPLICATION',
  COLLABORATOR = 'COLLABORATOR',
  USER = 'USER',
  CLUSTER = 'CLUSTER',
}

type ActivityItemData = {
  icon: React.FC<IconProps>;
  title: string;
};

type GroupedActivityData = {
  auditLogEventTypeGroup: AuditLogEventTypeGroup;
  recentTimestamp?: string;
  resourceId: string;
  baseUrl: string;
  activities: AuditAPIDataType[];
};

const AuditLogEventTypeGroupRange: Record<AuditLogEventTypeGroup, [number, number]> = {
  [AuditLogEventTypeGroup.UNKNOWN]: [0, 0],
  [AuditLogEventTypeGroup.ORGANIZATION]: [100, 199],
  [AuditLogEventTypeGroup.MODULE]: [200, 299],
  [AuditLogEventTypeGroup.MODEL]: [300, 399],
  [AuditLogEventTypeGroup.WORKFLOW]: [400, 499],
  [AuditLogEventTypeGroup.APPLICATION]: [600, 699],
  [AuditLogEventTypeGroup.COLLABORATOR]: [700, 799],
  [AuditLogEventTypeGroup.USER]: [800, 899],
  [AuditLogEventTypeGroup.CLUSTER]: [900, 1199],
};

const MapAuditLogEventTypeGroupWithActivityItemData: Record<AuditLogEventTypeGroup, ActivityItemData> = {
  [AuditLogEventTypeGroup.ORGANIZATION]: {
    icon: IconOrganization,
    title: 'Organization',
  },
  [AuditLogEventTypeGroup.MODULE]: {
    icon: IconModule,
    title: 'Module',
  },
  [AuditLogEventTypeGroup.MODEL]: {
    icon: IconCPU,
    title: 'Model',
  },
  [AuditLogEventTypeGroup.WORKFLOW]: {
    icon: IconWorkflow,
    title: 'Workflow',
  },
  [AuditLogEventTypeGroup.APPLICATION]: {
    icon: IconOverview,
    title: 'Application',
  },
  [AuditLogEventTypeGroup.COLLABORATOR]: {
    icon: IconUser,
    title: 'Collaborator',
  },
  [AuditLogEventTypeGroup.USER]: {
    icon: IconUser,
    title: 'User',
  },
  [AuditLogEventTypeGroup.CLUSTER]: {
    icon: IconServer,
    title: 'Cluster',
  },
  [AuditLogEventTypeGroup.UNKNOWN]: {
    icon: IconCPU, // Default icon for unknown types
    title: 'Unknown Activity',
  },
};

function auditLogTargetsToObject(targets?: ApiAuditLogTarget[]) {
  return (
    targets?.reduce((acc, target) => {
      return {
        ...acc,
        ...target,
      };
    }, {}) || {}
  );
}

function getAuditLogEventTypeGroup(operation: number): AuditLogEventTypeGroup {
  for (const [group, range] of Object.entries(AuditLogEventTypeGroupRange)) {
    if (operation >= range[0] && operation <= range[1]) {
      return AuditLogEventTypeGroup[group as keyof typeof AuditLogEventTypeGroup];
    }
  }

  return AuditLogEventTypeGroup.UNKNOWN;
}

function isResourceDeleted(groupedData: GroupedActivityData) {
  return groupedData.activities.some((activity) => activity.description?.includes('delete'));
}

function orderByTimestamp(a: AuditAPIDataType, b: AuditAPIDataType): number {
  return dayjs(b.timestamp).diff(dayjs(a.timestamp));
}

function orderByRecentTimestamp(a: GroupedActivityData, b: GroupedActivityData): number {
  return dayjs(b.recentTimestamp).diff(dayjs(a.recentTimestamp));
}

function replaceVerbsWithEd(input: string): string {
  return input.replace(/\b(updat|delet|creat|accept|invit)e?\b/gi, (_, base) => `${base}ed`);
}

function getBaseURLAndIDOfResource(auditLogEventTypeGroup: AuditLogEventTypeGroup, ownerUserId: string, targets?: ApiAuditLogTarget[]) {
  const target = auditLogTargetsToObject(targets);

  const workflowId = target.workflow?.id;
  const modelId = target.model?.id;
  const moduleId = target.module?.id;
  const appId = target.app?.id;

  const userAppPrefix = `/${ownerUserId}/${appId}`;
  const clusterId = target.compute_cluster?.id;

  switch (auditLogEventTypeGroup) {
    case AuditLogEventTypeGroup.ORGANIZATION:
      return {
        baseUrl: `/${ownerUserId}/settings`,
        resourceId: ownerUserId,
      };
    case AuditLogEventTypeGroup.MODULE: {
      return {
        baseUrl: `${userAppPrefix}/modules/${moduleId}`,
        resourceId: moduleId,
      };
    }
    case AuditLogEventTypeGroup.MODEL: {
      return {
        baseUrl: `${userAppPrefix}/models/${modelId}`,
        resourceId: modelId,
      };
    }
    case AuditLogEventTypeGroup.WORKFLOW: {
      return {
        baseUrl: `${userAppPrefix}/workflows/${workflowId}`,
        resourceId: workflowId,
      };
    }
    case AuditLogEventTypeGroup.APPLICATION:
      return {
        baseUrl: userAppPrefix,
        resourceId: appId,
      };
    case AuditLogEventTypeGroup.COLLABORATOR:
      return {};
    case AuditLogEventTypeGroup.USER:
      return {
        baseUrl: `/${ownerUserId}`,
        resourceId: ownerUserId,
      };
    case AuditLogEventTypeGroup.CLUSTER:
      return {
        baseUrl: `/compute/clusters/${clusterId}`,
        resourceId: clusterId,
      };
    default:
      return {};
  }
}

function ResourceId({ groupedData }: { groupedData: GroupedActivityData }) {
  const { baseUrl, resourceId } = groupedData;

  return (
    <div className="resource-id-wrapper">
      {baseUrl ? (
        <Link href={baseUrl} className={link}>
          {resourceId}
        </Link>
      ) : (
        <>{resourceId}</>
      )}
    </div>
  );
}

function ActivityItem({ activity }: { activity: AuditAPIDataType }) {
  const fullname = activity.user ? makeUserFullName(activity.user as CF.LocalUser) : '';
  const initials = makeInitials(fullname);
  const activityRelativeTime = dayjs(activity.timestamp).fromNow();

  return (
    <li>
      {activity.user && (
        <TextTooltip text={fullname} showArrow arrowProps={{ className: darkArrow }}>
          <TextIcon text={initials} background={stringToColor(fullname)} size={24} />
        </TextTooltip>
      )}
      <div className="details">{activity.details?.join(', ')}</div>
      <div className="timestamp" style={{ marginRight: '3rem' }}>
        {activityRelativeTime}
      </div>
    </li>
  );
}

function ActivityGroup({ groupedData }: { groupedData: GroupedActivityData }) {
  const activityItemData = MapAuditLogEventTypeGroupWithActivityItemData[groupedData.auditLogEventTypeGroup];

  if (!activityItemData) {
    return null;
  }

  const { icon: Icon, title } = activityItemData;
  const groupRelativeTime = dayjs(groupedData.recentTimestamp).fromNow();

  return (
    <Accordion
      styles={{
        label: { padding: 0, minWidth: 0 },
        item: { border: 0 },
        chevron: {
          minWidth: '1.5rem',
          marginLeft: '1rem',
          marginRight: '0.5rem',
        },
      }}
      className="grouped-activity-item"
    >
      <Accordion.Item value={`${groupedData.baseUrl}-${groupedData.resourceId}`}>
        <Accordion.Control>
          <div className="grouped-activity-item-content">
            <div className="grouped-activity-item-title">
              <div className="icon-wrapper">
                <Icon size={16} />
              </div>
              <strong>{title}:</strong>
              <div style={{ flex: 1, maxWidth: '100%', display: 'flex', minWidth: 0 }}>
                <ResourceId groupedData={groupedData} />
              </div>
            </div>
            <div className="timestamp">{groupRelativeTime}</div>
          </div>
        </Accordion.Control>
        <Accordion.Panel>
          <ul className="activity-item-list">
            {groupedData.activities.map((activity) => (
              <ActivityItem key={`${groupedData.resourceId}-item-${activity.timestamp}`} activity={activity} />
            ))}
          </ul>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export function RecentActivity() {
  const hasAccess = useHasAuditLogAccess();
  const { data, isLoading, error } = useGroupedAuditLogData();

  const reactLeft = error && apiErrorToReactLeft(error);
  const errorUI = reactLeft && reactLeftToJSX()(reactLeft);
  const emptyUI = !data?.length && <div className="empty">No recent activity found.</div>;

  if (!hasAccess || error) {
    return null;
  }

  return (
    <HomeCard title="Your Recent Activity">
      <ul className={groupedActivityListStyles}>
        {data?.map((groupedData) => (
          <ActivityGroup key={`${groupedData.resourceId}-${groupedData.recentTimestamp}`} groupedData={groupedData} />
        ))}
        {!isLoading && (errorUI || emptyUI)}
        <LoadingOverlay visible={isLoading} loader={<Loader variant="dots" />} />
      </ul>
    </HomeCard>
  );
}

function useGroupedAuditLogData() {
  const userId = useCurrentUserId();
  const startOfPastSevenDays = today.subtract(7, 'days').startOf('day').toISOString();
  const endOfToday = today.endOf('day').toISOString();

  const { data, isLoading, error } = useAuditLogData({
    targetApps: null,
    timestamp_from: startOfPastSevenDays,
    timestamp_to: endOfToday,
    userId,
  });

  const groupedData: GroupedActivityData[] = (data || [])
    .filter((auditLog) => auditLog.success)
    .reduce((grouped, auditLog) => {
      const auditLogEventTypeGroup = getAuditLogEventTypeGroup(auditLog.operation as unknown as number);
      const { baseUrl, resourceId } = getBaseURLAndIDOfResource(auditLogEventTypeGroup, userId, auditLog.targets);

      if (!baseUrl || !resourceId) {
        return grouped;
      }

      auditLog.details = auditLog.details?.map(replaceVerbsWithEd);

      const existingGroupIndex = grouped.findIndex((group) => group.baseUrl === baseUrl);

      if (existingGroupIndex >= 0) {
        grouped[existingGroupIndex].activities.push(auditLog);
        return grouped;
      }

      grouped.push({
        auditLogEventTypeGroup: getAuditLogEventTypeGroup(auditLog.operation as unknown as number),
        recentTimestamp: auditLog.timestamp,
        resourceId,
        baseUrl,
        activities: [auditLog],
      });

      return grouped;
    }, [] as GroupedActivityData[])
    .filter((group) => {
      // Filter out groups that do not have any activity or are of unknown type
      return group.activities.length > 0 && group.auditLogEventTypeGroup !== AuditLogEventTypeGroup.UNKNOWN;
    })
    .map((group) => {
      group.activities = group.activities.sort(orderByTimestamp);
      group.recentTimestamp = group.activities[0].timestamp;

      if (isResourceDeleted(group)) {
        group.baseUrl = '';
      }

      return group;
    })
    .sort(orderByRecentTimestamp);

  return { data: groupedData.slice(0, 5), isLoading, error };
}
